import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import DocNoLink from '../DocNoLink';
import { TAB_GROUPS } from '../../tabPalette';

// Full receipt-email composer (opened after a payment is matched, or from the ✈ icon): sender, recipient,
// Cc, SUBJECT and BODY are all pre-filled from TPL-022 and fully editable; the receipt PDF is attached.
// Mirrors the invoice editor (prepareReceiptEmail + sendReceiptEmailCustom).

const validEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

function EmailChips({ value, onChange, ph }: { value: string[]; onChange: (v: string[]) => void; ph: string }) {
  const [q, setQ] = useState('');
  const add = (raw: string) => { const v = raw.trim().toLowerCase(); if (v && validEmail(v) && !value.includes(v)) onChange([...value, v]); setQ(''); };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', padding: '6px 8px', border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--c-gray-50)' }}>
      {value.map(e => (
        <span key={e} style={{ fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 8, background: 'var(--accent-bg)', color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          {e}<span onClick={() => onChange(value.filter(x => x !== e))} style={{ cursor: 'pointer', fontWeight: 800 }}>×</span>
        </span>
      ))}
      <input value={q} onChange={e => setQ(e.target.value)} placeholder={ph}
        onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && q.trim()) { e.preventDefault(); add(q); } }}
        onBlur={() => q.trim() && add(q)}
        style={{ flex: 1, minWidth: 140, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--text-primary)' }} />
    </div>
  );
}

export default function ReceiptEmailComposer({ receiptId, defaultName, onClose, onSent }: {
  receiptId: string; defaultName?: string; onClose: () => void; onSent?: () => void;
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [initialBody, setInitialBody] = useState('');
  const [includePdf, setIncludePdf] = useState(true);
  const [attachmentName, setAttachmentName] = useState('receipt.pdf');
  const [docNo, setDocNo] = useState('');
  const [warning, setWarning] = useState('');
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Preview the receipt PDF (the attachment) in a new tab.
  const preview = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try { await api.openReceiptPdf(receiptId); } catch (e: any) { setErr(e?.message || 'Preview failed'); } finally { setPdfBusy(false); }
  };

  useEffect(() => {
    let alive = true;
    api.prepareReceiptEmail(receiptId, defaultName).then((d: any) => {
      if (!alive) return;
      setFrom(d.from || ''); setTo(d.to || []); setCc(d.cc || []);
      setSubject(d.subject || ''); setInitialBody(d.bodyHtml || '');
      setAttachmentName(d.attachmentName || 'receipt.pdf'); setDocNo(d.docNo || ''); setWarning(d.warning || '');
    }).catch((e: any) => alive && setErr(e?.message || 'Failed to prepare email')).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [receiptId, defaultName]);

  const send = async () => {
    const body = bodyRef.current ? bodyRef.current.innerHTML : initialBody;
    if (!to.length) { setErr(t('iv_email_need')); return; }
    setBusy(true); setErr('');
    try { await api.sendReceiptEmailCustom(receiptId, { from, to, cc, subject, bodyHtml: body, includePdf }); setDone(true); onSent?.(); setTimeout(onClose, 2600); }
    catch (e: any) { setErr(e?.message || 'Failed to send'); } finally { setBusy(false); }
  };

  const inp: React.CSSProperties = { width: '100%', padding: '9px 11px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-secondary)', display: 'block', marginBottom: 5 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10003, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 600, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, padding: 22, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{t('iv_email_title')}</div>
          {/* The number in the Quotation / Invoice / Receipt blue on that box's wash, and a link to its receipt (Calem,
              Bugify #b4cccf02 — see DocNoLink). Was the app's green accent on its green wash. */}
          {docNo && <span style={{ fontSize: 12, fontWeight: 700, background: TAB_GROUPS.peak.tint, padding: '4px 11px', borderRadius: 999 }}><DocNoLink no={docNo} id={receiptId} kind="receipt" /></span>}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3 }}>{t('iv_email_cc')}</div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} /></div>
        ) : (
          <div style={{ marginTop: 14 }}>
            {warning && <div style={{ fontSize: 12.5, color: '#92400E', background: '#FEF3C7', borderRadius: 9, padding: '8px 11px', marginBottom: 12 }}><i className="ti ti-alert-triangle" style={{ marginRight: 6 }} />{warning}</div>}

            <label style={lbl}>{t('bie_from')}</label>
            <input value={from} onChange={e => setFrom(e.target.value)} style={inp} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_to')}</label>
            <EmailChips value={to} onChange={setTo} ph={t('bie_email_ph')} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_cc')}</label>
            <EmailChips value={cc} onChange={setCc} ph={t('bie_email_ph')} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_subject')}</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} style={inp} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_body')}</label>
            <div ref={bodyRef} contentEditable suppressContentEditableWarning
              dangerouslySetInnerHTML={{ __html: initialBody }}
              style={{ minHeight: 160, maxHeight: 300, overflowY: 'auto', padding: '11px 13px', fontSize: 13.5, lineHeight: 1.6, border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none' }} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_attachment')}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: 'var(--text-primary)' }}>
              <input type="checkbox" checked={includePdf} onChange={e => setIncludePdf(e.target.checked)} style={{ cursor: 'pointer' }} />
              <i className="ti ti-file-type-pdf" style={{ color: '#E24B4A', fontSize: 16 }} />
              {/* Click the filename (or the eye) to preview the receipt PDF in a new tab. */}
              <span onClick={preview} title={t('bs_open_receipt')} style={{ fontWeight: 600, color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' }}>{attachmentName}</span>
              <button onClick={preview} disabled={pdfBusy} title={t('bs_open_receipt')} style={{ border: 'none', background: 'none', cursor: pdfBusy ? 'default' : 'pointer', color: 'var(--text-tertiary)', display: 'inline-flex', alignItems: 'center' }}>
                <i className={pdfBusy ? 'ti ti-loader' : 'ti ti-eye'} style={{ fontSize: 15 }} />
              </button>
            </div>

            {err && <div style={{ fontSize: 12.5, color: '#E24B4A', marginTop: 12 }}>{err}</div>}
            {done ? (
              <div style={{ fontSize: 13.5, color: '#B45309', fontWeight: 700, marginTop: 16 }}><i className="ti ti-mail-pause" style={{ marginRight: 6 }} />{t('oe_queued')}</div>
            ) : (
              <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
                <button onClick={send} disabled={busy} style={{ padding: '9px 22px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 800, fontSize: 13.5, cursor: 'pointer', opacity: busy ? 0.7 : 1 }}>{busy ? t('login_wait') : t('iv_email_send')}</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
